/**
 * B.H.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../PageShell';
import { logout } from '../../features/user';
import axios from 'axios';
import { Dialog } from '@mui/material';
import { API_URL, API_OK, API_EXCEPTION, DEFAULT_ERROR_TEXT } from '../../Constants';
import '../../styles.css';
import cookie from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showErrorDialog } from '../../features/errorDialog';
import { handleApiErrorMessage, formatLocalPhone } from '../../Helper';

// תוויות עבריות לעמודות הלוג
const CODE_LABELS = { CREATE: 'הוספה', UPDATE: 'עדכון', DELETE: 'מחיקה', TO_ACTIVE: 'הפעלה', TO_INACTIVE: 'השבתה' };
const CALL_TYPE_LABELS = { OUT: 'יוצאת', IN: 'נכנסת', ALL: 'הכל' };
// מצב אפקטיבי: BLOCKED = חסום, וכל דבר אחר (OPEN / ריק) = פתוח.
// מציגים חץ-מעבר רק כשהמצב האפקטיבי באמת השתנה (אחרת לא "פתוח ← פתוח").
const statusChangeText = (row) => {
  if (row.code === 'TO_ACTIVE' || row.code === 'TO_INACTIVE') return '—';
  const oldB = row.old_status === 'BLOCKED';
  const newB = row.new_status === 'BLOCKED';
  const lbl = (b) => (b ? 'חסום' : 'פתוח');
  return oldB === newB ? lbl(newB) : `${lbl(oldB)} ← ${lbl(newB)}`;
};

const PAGE_SIZE = 50;

export default function BlockHistory(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [number, setNumber] = useState('');
  const [filters, setFilters] = useState({ code: '', call_type: '', old_status: '', new_status: '' });
  const [order, setOrder] = useState('DESC');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const fetchLogs = useCallback((pageToLoad, opts = {}) => {
    const activeNumber = opts.number !== undefined ? opts.number : number;
    const activeFilters = opts.filters !== undefined ? opts.filters : filters;
    const activeOrder = opts.order !== undefined ? opts.order : order;

    setLoading(true);
    setError('');
    const headers = { token: cookie.get('token') };
    const cleanFilters = {};
    Object.keys(activeFilters).forEach((k) => { if (activeFilters[k]) cleanFilters[k] = activeFilters[k]; });

    const body = {
      number: activeNumber.trim() || undefined,
      filters: cleanFilters,
      order: activeOrder,
      limit: PAGE_SIZE,
      offset: pageToLoad * PAGE_SIZE,
    };

    axios.post(API_URL + '/searchBlockListLogs', body, { headers })
      .then(({ data }) => {
        if (data.responseStatus === API_OK) {
          setRows(data.result?.rows || []);
          setTotal(data.result?.total || 0);
          setPage(pageToLoad);
          setSearched(true);
        } else {
          const msg = (data.responseStatus === API_EXCEPTION && data.message) ? data.message : t(DEFAULT_ERROR_TEXT);
          setError(msg);
          handleApiErrorMessage(msg, t, dispatch, showErrorDialog, logout, navigate);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(t(DEFAULT_ERROR_TEXT));
        handleApiErrorMessage(t(DEFAULT_ERROR_TEXT), t, dispatch, showErrorDialog, logout, navigate);
        setLoading(false);
      });
  }, [number, filters, order, dispatch, navigate, t]);

  // טעינה ראשונית - עיון בכל היסטוריית הקהילה
  useEffect(() => { fetchLogs(0); /* eslint-disable-next-line */ }, []);

  const runSearch = () => fetchLogs(0);

  const changeFilter = (field, value) => {
    const next = { ...filters, [field]: value };
    setFilters(next);
    fetchLogs(0, { filters: next });
  };

  const changeOrder = (value) => {
    setOrder(value);
    fetchLogs(0, { order: value });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total;

  const inner = (
    <>
      <div className="bsearch">
        <div className="bsearch-head">
          <span className="bsearch-head-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/></svg>
          </span>
          <div className="bsearch-titles">
            <h3 className="bsearch-title">חיפוש הסטוריית חסימה</h3>
            <p className="bsearch-sub">היסטוריית ההוספות, העריכות והמחיקות ברשימות החסימה. חיפוש לפי מספר (אופציונלי) או עיון בכל ההיסטוריה, עם סינון ומיון.</p>
          </div>
        </div>

        <div className="bsearch-bar">
          <div className="bsearch-input-wrap">
            <svg className="bsearch-input-icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              className="bsearch-input"
              type="text"
              dir="ltr"
              placeholder="הקלידו מספר לחיפוש (או השאירו ריק לעיון בכל ההיסטוריה)"
              value={number}
              onChange={(e) => { if (/^[0-9*+]*$/.test(e.target.value)) setNumber(e.target.value); }}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            />
          </div>
          <button className="bsearch-btn" onClick={runSearch} disabled={loading}>
            {loading ? <span className="bsearch-btn-spin" /> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>}
            חיפוש
          </button>
        </div>

        {/* מסננים + מיון */}
        <div className="bl-field-row bhist-filters" style={{ flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
          <div className="bl-field" style={{ minWidth: '140px', flex: '1 1 140px' }}>
            <label>פעולה</label>
            <select value={filters.code} onChange={(e) => changeFilter('code', e.target.value)}>
              <option value="">הכל</option>
              {Object.keys(CODE_LABELS).map((k) => <option key={k} value={k}>{CODE_LABELS[k]}</option>)}
            </select>
          </div>
          <div className="bl-field" style={{ minWidth: '140px', flex: '1 1 140px' }}>
            <label>סוג שיחה</label>
            <select value={filters.call_type} onChange={(e) => changeFilter('call_type', e.target.value)}>
              <option value="">הכל</option>
              <option value="OUT">יוצאת</option>
              <option value="IN">נכנסת</option>
            </select>
          </div>
          <div className="bl-field" style={{ minWidth: '140px', flex: '1 1 140px' }}>
            <label>סטטוס קודם</label>
            <select value={filters.old_status} onChange={(e) => changeFilter('old_status', e.target.value)}>
              <option value="">הכל</option>
              <option value="BLOCKED">חסום</option>
              <option value="OPEN">פתוח</option>
            </select>
          </div>
          <div className="bl-field" style={{ minWidth: '140px', flex: '1 1 140px' }}>
            <label>סטטוס חדש</label>
            <select value={filters.new_status} onChange={(e) => changeFilter('new_status', e.target.value)}>
              <option value="">הכל</option>
              <option value="BLOCKED">חסום</option>
              <option value="OPEN">פתוח</option>
            </select>
          </div>
          <div className="bl-field" style={{ minWidth: '140px', flex: '1 1 140px' }}>
            <label>מיון</label>
            <select value={order} onChange={(e) => changeOrder(e.target.value)}>
              <option value="DESC">חדש קודם</option>
              <option value="ASC">ישן קודם</option>
            </select>
          </div>
        </div>

        {error && <div className="bsearch-error">{error}</div>}

        {searched && (
          <div className="bsearch-results">
            {rows.length === 0 ? (
              <div className="bsearch-empty">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span>לא נמצאו רשומות היסטוריה.</span>
              </div>
            ) : (
              <>
                <div className="bsearch-summary">
                  נמצאו <strong>{total}</strong> רשומות · עמוד {page + 1} מתוך {totalPages}
                </div>
                <div className="bsearch-list">
                  {rows.map((row) => (
                    <div className="bsearch-item bhist-item" key={row.id} role="button" onClick={() => setDetailRow(row)} title="לחצו לפרטים מלאים">
                      <div className="bsearch-item-info">
                        <div className="bsearch-item-top">
                          <span className={'bsearch-status' + (row.code === 'DELETE' ? ' is-blocked' : ' is-open')}>
                            {CODE_LABELS[row.code] || row.code}
                          </span>
                          <span className="bsearch-item-num" dir="ltr">{formatLocalPhone(row.number)}</span>
                          {row.isRange && <span className="bsearch-tag bsearch-tag--range" title="טווח / קידומת">טווח</span>}
                          <span className="bsearch-tag">{CALL_TYPE_LABELS[row.call_type] || row.call_type}</span>
                        </div>
                        <div className="bsearch-item-bottom" style={{ flexWrap: 'wrap', gap: '6px 14px' }}>
                          {row.code !== 'TO_ACTIVE' && row.code !== 'TO_INACTIVE' && (row.old_status || row.new_status) && (
                            <span className="bsearch-source">
                              סטטוס: {statusChangeText(row)}
                            </span>
                          )}
                          <span className="bsearch-source is-main">בוצע ע"י {row.create_by || '—'}</span>
                          <span className="bsearch-note">{new Date(row.create_date).toLocaleString('he-IL')}</span>
                          {row.action_note && <span className="bsearch-note" title="הערת ביצוע">📝 {row.action_note}</span>}
                        </div>
                      </div>
                      <svg className="bhist-item-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                    </div>
                  ))}
                </div>

                {/* דפדוף */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
                  <button className="bl-dialog-cancel" disabled={!hasPrev || loading} onClick={() => fetchLogs(page - 1)}>הקודם</button>
                  <span className="bsearch-sub" style={{ margin: 0 }}>עמוד {page + 1} / {totalPages}</span>
                  <button className="bl-dialog-cancel" disabled={!hasNext || loading} onClick={() => fetchLogs(page + 1)}>הבא</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* פופאפ פירוט מלא של רשומת ההיסטוריה */}
      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} PaperProps={{ className: 'bl-dialog-paper' }}>
        {detailRow && (
          <div className="bl-dialog">
            <div className={'bl-dialog-header' + (detailRow.code === 'DELETE' ? ' bl-dialog-header--danger' : '')}>
              <div className="bl-dialog-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/></svg>
              </div>
              <h3 className="bl-dialog-title">פרטי פעולה — {CODE_LABELS[detailRow.code] || detailRow.code}</h3>
              <button className="bl-dialog-close" onClick={() => setDetailRow(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="bl-dialog-body">
              <div className="bhist-detail-grid">
                <div className="bhist-detail-cell"><span className="bhist-detail-lbl">פעולה</span><span className="bhist-detail-val">{CODE_LABELS[detailRow.code] || detailRow.code}</span></div>
                <div className="bhist-detail-cell"><span className="bhist-detail-lbl">מספר / טווח</span><span className="bhist-detail-val" dir="ltr">{formatLocalPhone(detailRow.number)}{detailRow.isRange ? ' (טווח)' : ''}</span></div>
                <div className="bhist-detail-cell"><span className="bhist-detail-lbl">סוג שיחה</span><span className="bhist-detail-val">{CALL_TYPE_LABELS[detailRow.call_type] || detailRow.call_type}</span></div>
                <div className="bhist-detail-cell"><span className="bhist-detail-lbl">שינוי סטטוס</span><span className="bhist-detail-val">{statusChangeText(detailRow)}</span></div>
                <div className="bhist-detail-cell"><span className="bhist-detail-lbl">בוצע ע"י</span><span className="bhist-detail-val">{detailRow.create_by || '—'}</span></div>
                <div className="bhist-detail-cell"><span className="bhist-detail-lbl">תאריך ושעה</span><span className="bhist-detail-val" dir="ltr">{new Date(detailRow.create_date).toLocaleString('he-IL')}</span></div>
              </div>

              <div className="bhist-detail-note">
                <span className="bhist-detail-lbl">הערת רשומה</span>
                <div className="bhist-detail-note-box">{detailRow.note || '— ללא הערה —'}</div>
              </div>
              <div className="bhist-detail-note">
                <span className="bhist-detail-lbl">הערת ביצוע</span>
                <div className="bhist-detail-note-box bhist-detail-note-box--action">{detailRow.action_note || '— לא צוינה הערת ביצוע —'}</div>
              </div>
            </div>
            <div className="bl-dialog-footer">
              <button className="bl-dialog-cancel" onClick={() => setDetailRow(null)}>סגירה</button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );

  // when embedded inside another page (e.g. the blocks-management tab) skip the PageShell chrome
  if (props.embedded) return inner;
  return (
    <PageShell title="חיפוש הסטוריית חסימה" subtitle="יומן הפעולות שבוצעו ברשימות החסימה של הקהילה">
      {inner}
    </PageShell>
  );
}
